import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  IconButton,
  Stack,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import React, { useEffect, useRef, useState } from 'react';
import ReactSelect, { MultiValue } from 'react-select';
import FilterIcon from '../../assets/icons/filter.svg';
export interface FilterDrawerProps {
  filterTitle?: string;
  filterOptions?: Record<string, { label: string; value: string }[]>;
  onApplyFilters: (
    filters: Record<string, string[] | string>
  ) => void;
  clearFilters?: boolean;
  initialFilters?: Record<string, string[] | string>;
}

const FilterDrawer: React.FC<FilterDrawerProps> = ({
  filterTitle,
  filterOptions = {},
  onApplyFilters,
  clearFilters,
  initialFilters,
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const btnRef = useRef<HTMLButtonElement>(null);
  const [selectedFilters, setSelectedFilters] = useState<
    Record<string, string>
  >({});
  const [selectedMultiFilters, setSelectedMultiFilters] = useState<
    Record<string, string[]>
  >({});
  const prevIsOpenRef = useRef(false);

  // Initialize filters when drawer opens (only on open, not on every initialFilters change)
  useEffect(() => {
    // Only initialize when drawer transitions from closed to open
    if (isOpen && !prevIsOpenRef.current) {
      if (initialFilters) {
        const singleFilters: Record<string, string> = {};
        const multiFilters: Record<string, string[]> = {};

        Object.entries(initialFilters).forEach(([key, value]) => {
          // Check if this should be a multi-select filter
          // For Plans, Practices, and Providers, Market and States should use react-select with multi-select
          const isMultiSelect =
            (filterTitle?.includes('Plans') || filterTitle === 'Practices' || filterTitle === 'Providers') &&
            (key === 'Market' || key === 'States' || key === 'Status' || key === 'Payor');

          if (isMultiSelect) {
            if (Array.isArray(value)) {
              multiFilters[key] = value;
            } else if (value) {
              // Handle backwards compatibility: single value as array
              multiFilters[key] = [value];
            }
          } else {
            // All single-select filters use react-select (searchable)
            if (Array.isArray(value)) {
              // For single-select, take first value
              if (value.length > 0) {
                singleFilters[key] = value[0];
              }
            } else {
              singleFilters[key] = value;
            }
          }
        });

        setSelectedFilters(singleFilters);
        setSelectedMultiFilters(multiFilters);
      } else {
        // Reset filters when opening without initial filters
        setSelectedFilters({});
        setSelectedMultiFilters({});
      }
    } else if (!isOpen && prevIsOpenRef.current) {
      // Reset filters when drawer closes
      setSelectedFilters({});
      setSelectedMultiFilters({});
    }

    // Update ref to track previous open state
    prevIsOpenRef.current = isOpen;
  }, [isOpen, initialFilters, filterTitle]);


  const handleSelectChange = (filterKey: string, value: string) => {
    if (value === '') {
      setSelectedFilters((prev) => {
        const newFilters = { ...prev };
        delete newFilters[filterKey];
        return newFilters;
      });
    } else {
      setSelectedFilters((prev) => ({
        ...prev,
        [filterKey]: value,
      }));
    }
  };


  const handleApplyFilters = () => {
    // Pass UI keys directly to onApplyFilters
    // The list components will handle conversion to API keys
    const uiFilters: Record<string, string | string[]> = {};

    // Handle single-select filters (keep as UI keys)
    Object.entries(selectedFilters).forEach(([uiKey, value]) => {
      if (value && value !== '') {
        uiFilters[uiKey] = value;
      }
    });

    // Handle multi-select filters (keep as UI keys)
    Object.entries(selectedMultiFilters).forEach(([uiKey, values]) => {
      if (values.length > 0) {
        uiFilters[uiKey] = values;
      }
    });

    onApplyFilters(uiFilters);
    onClose();
  };

  useEffect(() => {
    if (clearFilters) {
      setSelectedFilters({});
      setSelectedMultiFilters({});
    }
  }, [clearFilters]);

  return (
    <>
      <IconButton
        aria-label="Filter"
        icon={<img src={FilterIcon} alt="Filter" />}
        onClick={onOpen}
      />
      <Drawer
        isOpen={isOpen}
        placement="right"
        onClose={onClose}
        finalFocusRef={btnRef}
        size="sm"
      >
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader>
            <Text fontSize="1.25rem" fontWeight="700" mb="2.06rem">
              Filter {filterTitle}
            </Text>
          </DrawerHeader>
          <DrawerBody>
            <Stack gap="1.81rem">
              {Object.entries(filterOptions).map(([key, options]) => {
                // Check if this filter should support multi-select
                // For Plans, Practices, and Providers, Market and States should be multi-select
                const isMultiSelect =
                  (filterTitle?.includes('Plans') ||
                    filterTitle === 'Practices' ||
                    filterTitle === 'Providers') &&
                  (key === 'Market' || key === 'States');
                const selectedValues = isMultiSelect
                  ? selectedMultiFilters[key] || []
                  : [];

                // All single-select filters use react-select (searchable)
                const selectedSingleValue = !isMultiSelect
                  ? selectedFilters[key] || ''
                  : '';

                // Ensure options is always an array
                const safeOptions = Array.isArray(options)
                  ? options
                  : [];

                return (
                  <Stack key={key}>
                    <Text
                      fontSize="0.875rem"
                      fontWeight="500"
                      mb="0.37rem"
                    >
                      {key}
                    </Text>
                    {safeOptions.length > 0 && (
                      <>
                        {isMultiSelect ? (
                          <Box>
                            <ReactSelect
                              isMulti
                              options={safeOptions}
                              value={safeOptions.filter((opt) =>
                                selectedValues.includes(opt.value)
                              )}
                              onChange={(
                                newValue: MultiValue<{
                                  label: string;
                                  value: string;
                                }>
                              ) => {
                                const values = newValue
                                  ? newValue.map((opt) => opt.value)
                                  : [];
                                setSelectedMultiFilters((prev) => {
                                  if (values.length === 0) {
                                    const updated = { ...prev };
                                    delete updated[key];
                                    return updated;
                                  }
                                  return {
                                    ...prev,
                                    [key]: values,
                                  };
                                });
                              }}
                              placeholder={`Select ${key}`}
                              isSearchable
                              isClearable
                              styles={{
                                control: (base, state) => ({
                                  ...base,
                                  minHeight: '2.5rem',
                                  borderColor: state.isFocused
                                    ? '#3175CE'
                                    : '#CBD5E0',
                                  boxShadow: state.isFocused
                                    ? '0 0 0 1px #3175CE'
                                    : 'none',
                                  '&:hover': {
                                    borderColor: '#3175CE',
                                  },
                                }),
                                placeholder: (base) => ({
                                  ...base,
                                  color: '#4A5568',
                                  fontSize: '0.875rem',
                                }),
                                option: (base, state) => ({
                                  ...base,
                                  backgroundColor: state.isFocused
                                    ? '#EBF4FF'
                                    : state.isSelected
                                    ? '#3175CE'
                                    : 'white',
                                  color: state.isSelected
                                    ? 'white'
                                    : '#2D3748',
                                  fontSize: '0.875rem',
                                  padding: '0.5rem 0.75rem',
                                  cursor: 'pointer',
                                }),
                                multiValue: (base) => ({
                                  ...base,
                                  backgroundColor: '#EBF4FF',
                                }),
                                multiValueLabel: (base) => ({
                                  ...base,
                                  color: '#2D3748',
                                  fontSize: '0.875rem',
                                }),
                                multiValueRemove: (base) => ({
                                  ...base,
                                  color: '#2D3748',
                                  ':hover': {
                                    backgroundColor: '#C53030',
                                    color: 'white',
                                  },
                                }),
                                menu: (base) => ({
                                  ...base,
                                  zIndex: 1000,
                                }),
                              }}
                            />
                          </Box>
                        ) : (
                          <Box>
                            <ReactSelect
                              key={`${key}-${safeOptions.length}`}
                              options={safeOptions}
                              value={
                                safeOptions.find(
                                  (opt) =>
                                    opt.value === selectedSingleValue
                                ) || null
                              }
                              onChange={(newValue) => {
                                const value = newValue
                                  ? newValue.value
                                  : '';
                                handleSelectChange(key, value);
                              }}
                              placeholder={`Select ${key}`}
                              isSearchable
                              isClearable
                              noOptionsMessage={() =>
                                'No options available'
                              }
                              styles={{
                                control: (base, state) => ({
                                  ...base,
                                  minHeight: '2.5rem',
                                  borderColor: state.isFocused
                                    ? '#3175CE'
                                    : '#CBD5E0',
                                  boxShadow: state.isFocused
                                    ? '0 0 0 1px #3175CE'
                                    : 'none',
                                  '&:hover': {
                                    borderColor: '#3175CE',
                                  },
                                }),
                                placeholder: (base) => ({
                                  ...base,
                                  color: '#4A5568',
                                  fontSize: '0.875rem',
                                }),
                                option: (base, state) => ({
                                  ...base,
                                  backgroundColor: state.isFocused
                                    ? '#EBF4FF'
                                    : state.isSelected
                                    ? '#3175CE'
                                    : 'white',
                                  color: state.isSelected
                                    ? 'white'
                                    : '#2D3748',
                                  fontSize: '0.875rem',
                                  padding: '0.5rem 0.75rem',
                                  cursor: 'pointer',
                                }),
                                singleValue: (base) => ({
                                  ...base,
                                  color: '#2D3748',
                                  fontSize: '0.875rem',
                                }),
                                menu: (base) => ({
                                  ...base,
                                  zIndex: 1000,
                                }),
                              }}
                            />
                          </Box>
                        )}
                      </>
                    )}
                  </Stack>
                );
              })}
            </Stack>
          </DrawerBody>
          <DrawerFooter>
            <Flex gap="0.5rem">
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button
                backgroundColor="#3175CE"
                _hover={{ bg: '#1A305D' }}
                onClick={handleApplyFilters}
              >
                <Text
                  color="white"
                  fontSize="1.125rem"
                  fontWeight="500"
                >
                  Apply Filters
                </Text>
              </Button>
            </Flex>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </>
  );
};

export default FilterDrawer;
